import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";

// Events
import EventManager from "./EventManager";

const StyledGrid = styled.canvas`
  position: absolute;
  border-radius: 2.4rem;
  top: 3px;
  left: 3px;
`;

// TODO: Try SVG Grid

const drawGrid = (width, height, widthUnit = 1, heightUnit, ctx) => {
  if (!width || !height) return;
  if (!heightUnit) {
    heightUnit = widthUnit;
  }
  const columns = Math.ceil(width / widthUnit);
  const rows = Math.ceil(height / heightUnit);

  ctx.save();
  ctx.strokeStyle = "rgba(255, 255, 255, 1)";
  ctx.lineWidth = 0.2;
  for (let i = 0; i < columns; i++) {
    ctx.beginPath();
    ctx.moveTo(i * widthUnit, 0);
    ctx.lineTo(i * widthUnit, height);
    ctx.stroke();
  }
  for (let j = 0; j < rows; j++) {
    ctx.beginPath();
    ctx.moveTo(0, j * heightUnit);
    ctx.lineTo(width, j * heightUnit);
    ctx.stroke();
  }
  ctx.restore();
};

const drawLine = (x0, y0, x1, y1, ctx) => {
  ctx.save();
  ctx.strokeStyle = "#ff0000";
  ctx.beginPath();
  ctx.moveTo(x0, y0);
  ctx.lineTo(x1, y1);
  ctx.stroke();
  ctx.restore();
};

const drawGuides = (point, width, height, ctx) => {
  if (Math.abs(point[0] - 0.5) < 0.05) {
    // Show vertical line
    drawLine(width / 2, 0, width / 2, height, ctx);
  }

  if (Math.abs(point[1] - 0.5) < 0.01) {
    // Show horizontal line
    drawLine(0, height / 2, width, height / 2, ctx);
  }
};

const drawSafearea = (bounds, viewport, ctx) => {
  if (!bounds || !ctx) return;

  if (bounds.length < 4) return;

  const [minX, minY, maxX, maxY] = bounds;

  ctx.save();

  ctx.fillStyle = "rgba(255, 0, 0, 0.32)";
  ctx.fillRect(0, 0, viewport.width, viewport.height);
  ctx.clearRect(minX, minY, maxX, maxY);

  ctx.restore();
};

const drawOverlay = (image, width, height, ctx) => {
  if (!ctx || !image) return;

  ctx.save();
  ctx.drawImage(image, 0, 0, width, height);
  ctx.restore();
};

export default function Grid({
  width,
  height,
  point,
  showGuides,
  config = {
    grid: {
      rows: 8,
      columsn: 8
    },
    guides: {
      safearea: null
    }
  }
}) {
  const [settings, changeSettings] = useState({
    rows: config.grid.rows,
    columns: config.grid.columns,
    safearea: config.guides.safearea,
    showGrid: false,
    showSaveareas: false,
    toggleOverlay: false
  });

  const [overlayImage, setOverlayImage] = useState(null);

  const canvasRef = useRef(null);
  const ctxRef = useRef(null);

  const toggleGrid = (msg) => {
    changeSettings((state) => ({
      ...state,
      showGrid: msg && msg.hasOwnProperty("value") ? msg.value : !state.showGrid
    }));
  };

  const toggleSafeareas = (msg) => {
    changeSettings((state) => ({
      ...state,
      showSafeareas:
        msg && msg.hasOwnProperty("value") ? msg.value : !state.showSafeareas
    }));
  };

  const toggleOverlay = (msg) => {
    changeSettings((state) => ({
      ...state,
      showOverlay:
        msg && msg.hasOwnProperty("value") ? msg.value : !state.toggleOverlay
    }));
  };

  // On Mount
  useEffect(() => {
    // Make Grid
    // grid.current.draw(width, height, config.grid.columns, config.grid.rows);
    ctxRef.current = canvasRef.current.getContext("2d");

    EventManager.on("toggleGrid", toggleGrid);
    EventManager.on("toggleSafeareas", toggleSafeareas);
    EventManager.on("toggleOverlay", toggleOverlay);

    if (config.overlay) {
      const image = new Image();

      image.onload = () => {
        setOverlayImage(image);
      };

      image.src = config.overlay;
    }

    return () => {
      EventManager.off("toggleGrid", toggleGrid);
      EventManager.off("toggleSafeareas", toggleSafeareas);
      EventManager.off("toggleOverlay", toggleOverlay);
    };
  }, []);

  const { showGrid, showSafeareas, showOverlay } = settings;

  const render = () => {
    if (!ctxRef.current) return;

    // Clear Canvas
    ctxRef.current.clearRect(0, 0, width, height);

    if (showSafeareas) {
      drawSafearea(settings.safearea, { width, height }, ctxRef.current);
    }

    if (showOverlay) {
      drawOverlay(overlayImage, width, height, ctxRef.current);
    }

    if (showGrid) {
      drawGrid(width, height, settings.columns, settings.rows, ctxRef.current);
    }

    if (showGuides) {
      drawGuides(point, width, height, ctxRef.current);
    }
  };

  render();

  return <StyledGrid ref={canvasRef} width={width} height={height} />;
}
